/**
 * Resolve bindle search terms against inventory items.
 * 
 * Parses a search string into individual terms, identifies which are "loose"
 * (unquoted) vs "exact" (quoted), and finds matching item names for each
 * loose term. Used to prompt users to convert loose terms to exact matches
 * before saving a bindle.
 */

export interface ResolvedTerm {
  /** The original term as it appears in the search string */
  original: string;
  /** Whether the term is already quoted (exact match) */
  isExact: boolean;
  /** Item names that match this term (only populated for loose terms) */
  matchedNames: string[];
}

/**
 * Parse a search string into terms, preserving quoted/unquoted distinction.
 * Terms are comma-separated. Quoted terms use double quotes for exact matching.
 */
export function parseSearchTerms(searchString: string): Array<{ term: string; isExact: boolean }> {
  const normalized = searchString.replace(/[\u201C\u201D\u2018\u2019]/g, '"');
  const terms: Array<{ term: string; isExact: boolean }> = [];
  // Split by comma (OR) first, then by + (AND) within each group
  const termRegex = /"([^"]+)"|([^,]+)/g;
  let m;
  while ((m = termRegex.exec(normalized)) !== null) {
    if (m[1]) {
      // Quoted term — exact match, don't split by +
      terms.push({ term: m[1].trim(), isExact: true });
    } else {
      const segment = (m[2] || '').trim();
      if (!segment) continue;
      // Split by + (AND operator) within this comma group
      const subTerms = segment.split('+').map(s => s.trim()).filter(Boolean);
      for (const sub of subTerms) {
        // Check if sub-term itself is quoted
        const quoteMatch = sub.match(/^"([^"]+)"$/);
        if (quoteMatch) {
          terms.push({ term: quoteMatch[1], isExact: true });
        } else {
          terms.push({ term: sub, isExact: false });
        }
      }
    }
  }
  return terms;
}

/**
 * Resolve loose search terms against a list of item names.
 * Returns only loose terms that have at least one inventory match.
 * Skips hashtag terms (e.g., #dairy) since those match categories/tags, not items.
 */
export function resolveLooseTerms(
  searchString: string,
  itemNames: string[]
): ResolvedTerm[] {
  const parsed = parseSearchTerms(searchString);
  const results: ResolvedTerm[] = [];

  for (const { term, isExact } of parsed) {
    // Skip exact terms — already resolved
    if (isExact) continue;

    // Skip hashtag terms — they match categories, not item names
    if (term.startsWith('#')) continue;

    const lower = term.toLowerCase();
    const matched = itemNames.filter(name =>
      name.toLowerCase().includes(lower)
    );

    // Only include if there are matches to resolve
    if (matched.length > 0) {
      results.push({
        original: term,
        isExact: false,
        matchedNames: matched,
      });
    }
  }

  return results;
}

/**
 * Build a new search string with selected terms converted to exact (quoted) form.
 * 
 * @param searchString - Original search string
 * @param conversions - Map of original loose term → chosen exact name (or null to keep loose)
 * @returns Updated search string
 */
export function applyTermConversions(
  searchString: string,
  conversions: Map<string, string | null>
): string {
  const parsed = parseSearchTerms(searchString);

  return parsed.map(({ term, isExact }) => {
    if (isExact) return `"${term.toLowerCase()}"`;

    const chosen = conversions.get(term);
    if (chosen !== undefined && chosen !== null) {
      return `"${chosen.toLowerCase()}"`;
    }
    return term.toLowerCase();
  }).join(', ');
}
